import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Gauge,
  MapPin,
  Network,
  PhoneCall,
  RadioTower,
  Router,
} from "lucide-react";
import type {
  AuditReceipt,
  ResponseOption,
  ScenarioStage,
  SnapshotStatus,
} from "../../domain";
import { StatusPill } from "../../components/StatusPill";
import {
  telecomActivity,
  telecomSnapshot,
  telecomStory,
  type TelecomTone,
} from "./telecomPresentation";

type TelecomDashboardProps = {
  stage: ScenarioStage;
  decisionId: ResponseOption["id"] | null;
  linkedEvidenceCount: number;
  receipts: AuditReceipt[];
  onOpenIncident: () => void;
  onOpenTrace: () => void;
  showTools: boolean;
};

const toneLabels: Record<TelecomTone, string> = {
  healthy: "Healthy",
  warning: "Warning",
  critical: "Action needed",
  neutral: "Recorded",
};

function tone(status: SnapshotStatus): TelecomTone {
  return status;
}

export function TelecomDashboard({
  stage,
  decisionId,
  linkedEvidenceCount,
  receipts,
  onOpenIncident,
  onOpenTrace,
  showTools,
}: TelecomDashboardProps) {
  const snapshot = telecomSnapshot(stage, decisionId);
  const story = telecomStory(stage, linkedEvidenceCount, decisionId);
  const activity = telecomActivity(stage, receipts);
  const campusTone = tone(snapshot.serviceSla.availability.status);
  const nextAction = story.actionTarget === "incident" ? onOpenIncident : onOpenTrace;

  return (
    <>
      <section className={`campus-status-band campus-status-band--${campusTone}`} aria-label="Consolidated operating status">
        <div className="campus-status-band__state">
          <span className="campus-status-band__icon"><CheckCircle2 aria-hidden="true" /></span>
          <div>
            <span>Day in the Life · finals week</span>
            <strong>North Academic Center Wi-Fi and voice outage</strong>
          </div>
        </div>
        <div className="campus-status-band__metric">
          <strong>{story.title}</strong>
          <span>current narrative step</span>
        </div>
        <div className="campus-status-band__metric">
          <strong>{story.nextAction}</strong>
          <span>next safe action</span>
        </div>
        <div className="campus-status-band__updated">
          <Clock3 aria-hidden="true" />
          <span>Local deterministic scenario</span>
          <strong>No external feed</strong>
        </div>
      </section>

      <section className="telecom-activity" aria-labelledby="telecom-activity-title">
        <header className="telecom-section-heading">
          <div>
            <p className="eyebrow"><Activity aria-hidden="true" /> One Day in the Life scenario</p>
            <h2 id="telecom-activity-title">North Academic Center outage during finals week</h2>
            <p><strong>{story.title}.</strong> {story.summary}</p>
          </div>
          <button className="button button--secondary" onClick={nextAction} type="button">
            {story.nextAction}
            <ArrowRight aria-hidden="true" />
          </button>
        </header>
        <dl className="day-in-life__facts">
          <div><dt>Beginning</dt><dd>A switch and trunk change disrupt Wi-Fi and campus voice in one academic building.</dd></div>
          <div><dt>Operator goal</dt><dd>Restore service safely while keeping evidence, ownership, and human approval attached.</dd></div>
          <div><dt>Verified ending</dt><dd>Confirm service recovery, re-register TEL-0318, and preserve the report and handoff receipts.</dd></div>
        </dl>
        <ol className="telecom-activity__list">
          {activity.map((event) => (
            <li key={event.id}>
              <span className={`telecom-activity__marker telecom-activity__marker--${event.tone}`} aria-hidden="true" />
              <time>{event.time}</time>
              <div>
                <strong>{event.title}</strong>
                <p>{event.detail}</p>
                {event.recordId && <span className="record-chip">{event.recordId}</span>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {showTools && <section className="telecom-tools" aria-labelledby="telecom-tools-title">
        <header className="telecom-section-heading">
          <div>
            <p className="eyebrow">Operating Snapshot</p>
            <h2 id="telecom-tools-title">SLA, VoIP, Wi-Fi, and Switch Health</h2>
            <p>Every value comes from the deterministic campus fixture, current scenario state, or a labeled local derivation.</p>
          </div>
        </header>

        <div className="telecom-widget-grid">
          <article className="telecom-widget">
            <header>
              <span className="telecom-widget__icon"><Gauge aria-hidden="true" /></span>
              <div>
                <span>Service and SLA</span>
                <h3>{snapshot.serviceSla.availability.value}{snapshot.serviceSla.availability.unit} available</h3>
              </div>
              <StatusPill
                label={toneLabels[tone(snapshot.serviceSla.availability.status)]}
                tone={tone(snapshot.serviceSla.availability.status)}
              />
            </header>
            <dl className="telecom-widget__facts">
              <div>
                <dt>Demonstration target</dt>
                <dd>{snapshot.serviceSla.target.value}{snapshot.serviceSla.target.unit}</dd>
              </div>
              <div>
                <dt>Observed</dt>
                <dd>{snapshot.serviceSla.availability.observedAt}</dd>
              </div>
              <div>
                <dt>Anchor</dt>
                <dd>{snapshot.serviceSla.availability.anchorIds[0]}</dd>
              </div>
            </dl>
            <p><strong>Source:</strong> {snapshot.serviceSla.availability.provenance.label}.</p>
          </article>

          <article className="telecom-widget">
            <header>
              <span className="telecom-widget__icon"><PhoneCall aria-hidden="true" /></span>
              <div>
                <span>VoIP system · campus voice</span>
                <h3>{snapshot.voice.registered.value} {snapshot.voice.registered.unit}</h3>
              </div>
              <StatusPill
                label={toneLabels[tone(snapshot.voice.registered.status)]}
                tone={tone(snapshot.voice.registered.status)}
              />
            </header>
            <dl className="telecom-widget__facts">
              <div>
                <dt>Canonical endpoint</dt>
                <dd>{snapshot.voice.affectedAnchor.label}</dd>
              </div>
              <div>
                <dt>Registration</dt>
                <dd>{snapshot.voice.affectedAnchor.value}</dd>
              </div>
              <div>
                <dt>Observed</dt>
                <dd>{snapshot.voice.registered.observedAt}</dd>
              </div>
            </dl>
            <p><strong>Source:</strong> {snapshot.voice.registered.provenance.label}.</p>
          </article>

          <article className="telecom-widget">
            <header>
              <span className="telecom-widget__icon"><RadioTower aria-hidden="true" /></span>
              <div>
                <span>Campus Wi-Fi</span>
                <h3>{snapshot.wifi.coverage.value} {snapshot.wifi.coverage.unit} buildings</h3>
              </div>
              <StatusPill
                label={toneLabels[tone(snapshot.wifi.coverage.status)]}
                tone={tone(snapshot.wifi.coverage.status)}
              />
            </header>
            <div className="coverage-map" aria-label="Representative building Wi-Fi state">
              <span className="coverage-map__cell is-healthy"><strong>South Dorm</strong><small>Full</small></span>
              <span className="coverage-map__cell is-healthy"><strong>Library</strong><small>Full</small></span>
              <span className={`coverage-map__cell ${snapshot.wifi.affectedAreas.status === "healthy" ? "is-healthy" : "is-warning"}`}>
                <strong>North Academic</strong>
                <small>{snapshot.wifi.affectedAreas.status === "healthy" ? "Full" : "Partial"}</small>
              </span>
              <span className="coverage-map__cell is-healthy"><strong>Science Hall</strong><small>Full</small></span>
            </div>
            <p><strong>Affected areas:</strong> {snapshot.wifi.affectedAreas.value}. <strong>Source:</strong> {snapshot.wifi.coverage.provenance.label}.</p>
            <button className="text-button" onClick={onOpenTrace} type="button">
              <MapPin aria-hidden="true" /> Trace the affected building
            </button>
          </article>

          <article className="telecom-widget">
            <header>
              <span className="telecom-widget__icon"><Router aria-hidden="true" /></span>
              <div>
                <span>Switch health</span>
                <h3>{snapshot.switchHealth.state.label}</h3>
              </div>
              <StatusPill
                label={String(snapshot.switchHealth.state.value)}
                tone={tone(snapshot.switchHealth.state.status)}
              />
            </header>
            <div className="switch-monitor">
              <button onClick={onOpenTrace} type="button">
                <span><strong>CR-SW-01</strong><small>South Dorm</small></span>
                <StatusPill label="Healthy" tone="healthy" />
              </button>
              <button className="is-focus" onClick={onOpenTrace} type="button">
                <span><strong>{snapshot.switchHealth.state.label}</strong><small>North Academic</small></span>
                <StatusPill
                  label={String(snapshot.switchHealth.state.value)}
                  tone={tone(snapshot.switchHealth.state.status)}
                />
              </button>
              <button onClick={onOpenTrace} type="button">
                <span><strong>CR-SW-07</strong><small>Administration</small></span>
                <StatusPill label="Healthy" tone="healthy" />
              </button>
            </div>
            <p><strong>Last check:</strong> {snapshot.switchHealth.lastCheck.value}. <strong>Source:</strong> {snapshot.switchHealth.state.provenance.label}.</p>
          </article>
        </div>

        <div className="integration-readiness" aria-label="Coming Soon external tools">
          <span><strong>SolarWinds / PRTG</strong><small>Coming Soon</small></span>
          <span><strong>Cisco Unified CM</strong><small>Coming Soon</small></span>
          <span><strong>Ekahau</strong><small>Coming Soon</small></span>
          <span><strong>SNMP monitoring</strong><small>Coming Soon</small></span>
        </div>
      </section>}

      {showTools && <div className="telecom-boundary-note">
        <Network aria-hidden="true" />
        <span>
          <strong>Local simulation:</strong> no vendor feed is connected. SolarWinds/PRTG monitoring,
          Cisco Unified CM, Ekahau, and SNMP are <strong>Coming Soon</strong> concepts.
          Response time remains a receipt-backed branch outcome, not a live vendor metric.
        </span>
      </div>}
    </>
  );
}
